import React from "react";
import { clsx } from "clsx";

import { CarouselNav } from "~components/shared/carousel";
import { type MediaConfig, Media, MediaFormat } from "~components/shared/media";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import {
    type CuratedItem,
    type VariationComponent,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentCarousel3Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentCarousel3Up.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.videoImage,
    MediaFormat.defaultImage,
];

const MEDIA_CONFIG: MediaConfig = {
    className: styles.media,
    desktopMaxWidth: 265,
    mobileMaxWidth: 224,
    showCaption: false,
    aspectRatio: 3 / 2,
};

const MAX_ITEMS = 3;

export const getItemsGroupedByMaxItems = (
    items: CuratedItem[],
    maxItems: number
) => {
    const output = [];
    for (let i = 0; i < items.length; i += maxItems) {
        output.push(items.slice(i, i + maxItems));
    }
    return output;
};

const Card = (props: { item: CuratedItem; storyIndex: number }) => {
    const { item, storyIndex } = props;
    const { url, labelDropdown } = item;

    return (
        <div key={url} className={styles.item}>
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <StoryBlock
                className={styles.content}
                story={item}
                storyIndex={storyIndex}
                labelDropdown={labelDropdown}
                isSecondaryStory
                isExtraSmallHeadline
                withDek={false}
            />
        </div>
    );
};

export default function LineupContentCarousel3Up(props: VariationComponent) {
    const { items = [] } = props;

    const integrity = integrityCheck({ ...props });
    if (integrity.length) return integrityError(integrity);

    if (!items?.length) return null;

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContentCarousel3Up}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={clsx([styles.mobile, styles.slide])}>
                {items.map((item, index) => (
                    <Card key={item.url} storyIndex={index + 1} item={item} />
                ))}
            </div>
            <div className={styles.notMobile}>
                <CarouselNav animatedTransition trackingName="carousel3Up">
                    {getItemsGroupedByMaxItems(items, MAX_ITEMS).map(
                        (group, groupIndex) => (
                            <div key={groupIndex} className={styles.slide}>
                                {group.map((item, index) => (
                                    <Card
                                        key={item.url}
                                        storyIndex={
                                            groupIndex * MAX_ITEMS + index + 1
                                        }
                                        item={item}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </CarouselNav>
            </div>
        </VariantBaseWithHeader>
    );
}
